import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, X, Keyboard } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";

export default function Scanner() {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [manual, setManual] = useState("");
  const scannerRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    return () => { stop(); };
    // eslint-disable-next-line
  }, []);

  const start = async () => {
    setActive(true);
    setTimeout(async () => {
      try {
        const html5 = new Html5Qrcode("scanner-box");
        instanceRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (decodedText) => { handleResult(decodedText); },
          () => {}
        );
      } catch (e) {
        toast.error("Could not start camera. Use manual entry below.");
        setActive(false);
      }
    }, 200);
  };

  const stop = async () => {
    if (instanceRef.current) {
      try { await instanceRef.current.stop(); } catch {}
      try { instanceRef.current.clear(); } catch {}
      instanceRef.current = null;
    }
    setActive(false);
  };

  const handleResult = async (decoded) => {
    await stop();
    // Accept either a full URL or raw order id
    const orderId = extractOrderId(decoded);
    if (!orderId) { toast.error("QR code not recognised"); return; }
    toast.success("Order detected");
    navigate(`/scan/${orderId}`);
  };

  const submitManual = (e) => {
    e.preventDefault();
    const id = extractOrderId(manual.trim());
    if (!id) { toast.error("Enter a valid order ID or scan URL"); return; }
    navigate(`/scan/${id}`);
  };

  return (
    <div data-testid="admin-scanner">
      <div className="overline text-zinc-500">Warehouse</div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-2">Order scanner</h1>
      <p className="text-sm text-zinc-500 mb-6">
        Point your camera at the QR code printed on an order receipt to update its delivery status.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold">Camera</div>
            {active
              ? <button onClick={stop} className="text-xs font-semibold px-3 py-1 rounded-full bg-zinc-900 text-white inline-flex items-center gap-1" data-testid="scanner-stop"><X size={12} /> Stop</button>
              : <button onClick={start} className="btn-primary text-xs inline-flex items-center gap-1" data-testid="scanner-start"><Camera size={12} /> Start scanning</button>}
          </div>
          <div id="scanner-box" ref={scannerRef}
               className="w-full aspect-square rounded-xl bg-zinc-50 border border-dashed border-zinc-200 flex items-center justify-center overflow-hidden">
            {!active && <div className="text-xs text-zinc-400 flex flex-col items-center gap-2"><Camera size={28} /> Camera preview will appear here</div>}
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold flex items-center gap-2"><Keyboard size={14} /> Manual entry</div>
          </div>
          <p className="text-xs text-zinc-500 mb-4">No camera? Type or paste the order ID or scan URL.</p>
          <form onSubmit={submitManual} className="flex gap-2">
            <input value={manual} onChange={(e) => setManual(e.target.value)}
                   placeholder="Order ID or /scan/..."
                   className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-sm" data-testid="scanner-manual-input" />
            <button className="btn-primary text-sm" data-testid="scanner-manual-submit">Open</button>
          </form>
        </div>
      </div>
    </div>
  );
}

function extractOrderId(text) {
  if (!text) return null;
  // UUID v4 regex
  const m = text.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
  return m ? m[0] : null;
}
